/**
 * @fileoverview Per-job Mail page (MOR-529).
 *
 * URL: /dashboard/jobs/:jobId/mail (?thread=<id> deep-links a thread)
 *
 * One mail surface, like a normal client: a list pane with a folder switcher
 * (All Mail / Unread / Sent / Drafts / Archive) beside a reading pane.
 * Selecting a sent message opens its thread in place; selecting a draft opens
 * the review card in the reading pane. Settings sit behind a gear toggle in
 * the header. Clicking a matched contact anywhere opens the full contact
 * detail panel.
 *
 * Orchestrates the mailbox lifecycle: opt-in enable → provisioning spinner →
 * error/retry → active. `getMailboxForJob` is reactive, so the provisioning
 * state flips to active on its own once the provisioning action finishes.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useQuery as useTanstackQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import {
  MessageSquare,
  Check,
  Copy,
  Mail,
  Pencil,
  RefreshCw,
  Settings,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import { extractErrorMessage } from "@/utils/errors";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";
import type { JobContactRow } from "@/components/jobs/phases/contacts/types";
import { ComposeReader } from "./ComposeReader";
import { DraftReader } from "./DraftReader";
import { EnableMailboxCard, MailboxAddressField } from "./EnableMailboxCard";
import { validateHandleClient } from "./EnableMailboxCard.helpers";
import { MailAgentChatPanel } from "./MailAgentChatPanel";
import { MailboxSettingsTab } from "./MailboxSettingsTab";
import { MailListPane, type MailFolder } from "./ThreadList";
import { ThreadView } from "./ThreadView";
import type { Mailbox, UserDraftListItem } from "./types";

const MAIL_ROUTE = "/_app/_auth/dashboard/_layout/jobs/$jobId/mail" as const;

export function JobMailboxPage({ jobId }: { jobId: Id<"jobs"> }) {
  const mailbox = useQuery(api.mail.queries.getMailboxForJob, { jobId }) as
    Mailbox | null | undefined;

  // Height contract: the dashboard layout's document scrolls, so full-height
  // surfaces pin themselves to the viewport minus the 4rem sticky header
  // (same contract as JobAgentView / GatherView).
  return (
    <div className="flex h-[calc(100vh-4rem)] min-h-0 flex-col overflow-hidden">
      {mailbox === undefined ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="size-6 text-muted-foreground" />
        </div>
      ) : mailbox === null ? (
        <EnableMailboxCard jobId={jobId} />
      ) : mailbox.status === "provisioning" ? (
        <ProvisioningCard />
      ) : mailbox.status === "deleting" ? (
        <DeletingCard />
      ) : mailbox.status === "error" ? (
        <ErrorCard jobId={jobId} error={mailbox.error} />
      ) : (
        <ActiveMailbox jobId={jobId} mailbox={mailbox} />
      )}
    </div>
  );
}

function ProvisioningCard() {
  return (
    <div className="flex flex-1 items-center justify-center bg-muted p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 rounded-lg border bg-white p-8 text-center shadow-sm dark:bg-card">
        <Spinner className="size-6 text-primary" />
        <h2 className="text-base font-semibold text-primary">
          Setting up mailbox...
        </h2>
        <p className="text-sm text-muted-foreground">
          This usually takes a few seconds. The page will update automatically.
        </p>
      </div>
    </div>
  );
}

/**
 * Teardown state (MOR-954): shown while the deprovision action + batched
 * purge run. When the row is finally deleted the reactive query returns
 * null and the EnableMailboxCard takes over — the "set up a fresh inbox"
 * flow needs no extra wiring.
 */
function DeletingCard() {
  return (
    <div className="flex flex-1 items-center justify-center bg-muted p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 rounded-lg border bg-white p-8 text-center shadow-sm dark:bg-card">
        <Spinner className="size-6 text-destructive" />
        <h2 className="text-base font-semibold text-destructive">
          Deleting inbox...
        </h2>
        <p className="text-sm text-muted-foreground">
          The email address and all of its mail are being permanently removed.
          Once this finishes you can set up a new inbox with a different
          address.
        </p>
      </div>
    </div>
  );
}

function ErrorCard({
  jobId,
  error,
}: {
  jobId: Id<"jobs">;
  error: string | null;
}) {
  const enableMailbox = useMutation(api.mail.mutations.enableMailbox);
  const mailInfo = useQuery(api.mail.queries.getMailProviderInfo, { jobId });
  const [handle, setHandle] = useState("");
  const [retrying, setRetrying] = useState(false);
  const validation = handle.length > 0 ? validateHandleClient(handle) : null;

  // Leaving the field empty retries with the auto-generated address; a typed
  // handle retries with that exact address (e.g. after "address taken").
  const handleRetry = async () => {
    if (handle.length > 0 && validation) {
      toast.error(validation);
      return;
    }
    setRetrying(true);
    try {
      await enableMailbox({
        jobId,
        customHandle: handle.length > 0 ? handle : undefined,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to retry provisioning"));
      setRetrying(false);
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center bg-muted p-6">
      <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-destructive/30 bg-white p-8 text-center shadow-sm dark:bg-card">
        <h2 className="text-base font-semibold text-destructive">
          Mailbox setup failed
        </h2>
        <p className="text-sm text-muted-foreground">
          {error || "Something went wrong while provisioning this mailbox."}
        </p>
        <MailboxAddressField
          handle={handle}
          onChange={setHandle}
          error={validation}
          domain={mailInfo?.provider === "ses" ? mailInfo.domain : null}
        />
        <Button onClick={handleRetry} disabled={retrying}>
          <RefreshCw className="mr-1.5 size-3.5" />
          {retrying ? "Retrying..." : "Retry"}
        </Button>
      </div>
    </div>
  );
}

function ActiveMailbox({
  jobId,
  mailbox,
}: {
  jobId: Id<"jobs">;
  mailbox: Mailbox;
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: MAIL_ROUTE });
  const selectedThreadId =
    (search.thread as Id<"mailThreads"> | undefined) ?? null;
  const draftParamId = (search.draft as Id<"mailDrafts"> | undefined) ?? null;
  const urlUserDraft = useQuery(
    api.mail.userDrafts.getUserDraft,
    draftParamId ? { draftId: draftParamId } : "skip",
  );

  // Settings visibility is URL-driven so the sidebar "Mail" link reliably
  // returns to the inbox. Toggling preserves any open thread in the URL.
  const settingsOpen = search.settings === true;
  const setSettingsOpen = (open: boolean) => {
    void navigate({
      to: "/dashboard/jobs/$jobId/mail",
      params: { jobId },
      search: {
        thread: selectedThreadId ?? undefined,
        settings: open ? true : undefined,
      },
    });
  };

  const pendingDrafts = useQuery(api.mail.drafts.pendingDraftCount, { jobId });
  const userDraftCount = useQuery(api.mail.userDrafts.userDraftCount, {
    jobId,
  });

  const [folder, setFolder] = useState<MailFolder>("all");
  const [selectedDraftId, setSelectedDraftId] =
    useState<Id<"mailDrafts"> | null>(null);
  const [composing, setComposing] = useState(false);
  // A user draft being resumed in the composer (MOR-783); keys the composer
  // so switching drafts remounts it with fresh seeded state.
  const [composeSeed, setComposeSeed] = useState<UserDraftListItem | null>(
    null,
  );
  const [selectedUserDraftId, setSelectedUserDraftId] =
    useState<Id<"mailDrafts"> | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  // Seeds the mail chat composer when the user clicks "Draft with Morgan"
  // from the new-email composer. Cleared when the chat panel is closed so it
  // doesn't re-seed on a later manual open.
  const [chatSeed, setChatSeed] = useState<string | null>(null);
  const startNewMailChat = useMutation(
    api.jobs.summarization.agents.mailAgent.publicMutations.startNewMailChat,
  );
  const [copied, setCopied] = useState(false);
  const [viewingContactId, setViewingContactId] =
    useState<Id<"jobContacts"> | null>(null);

  // For the full contact panel (merge picker needs the job's contact rows).
  const { data: jobContacts } = useTanstackQuery(
    convexQuery(api.contacts.queries.listJobContacts, { jobId }),
  );

  const setSelectedThread = (threadId: Id<"mailThreads"> | null) => {
    // Opening a thread replaces the reader pane, so leave the composer.
    if (threadId) setComposing(false);
    // `to` must be the URL path — the MAIL_ROUTE id (with its pathless
    // _app/_auth/_layout segments) is only valid for `useSearch({ from })`;
    // navigating to it 404s.
    void navigate({
      to: "/dashboard/jobs/$jobId/mail",
      params: { jobId },
      search: { thread: threadId ?? undefined },
    });
  };

  const handleSelectDraft = (draftId: Id<"mailDrafts"> | null) => {
    if (draftId) setComposing(false);
    setSelectedDraftId(draftId);
  };

  // Consume a ?draft= deep link (from the workspace Drafts folder): a reply
  // draft opens its thread (the reply composer resumes it); a compose draft
  // opens the full composer seeded from it. The param is cleared afterwards
  // so refresh/back behaves.
  useEffect(() => {
    if (!draftParamId || urlUserDraft === undefined) return;
    if (urlUserDraft === null) {
      toast.error("That draft is no longer available.");
    } else if (urlUserDraft.threadId) {
      void navigate({
        to: "/dashboard/jobs/$jobId/mail",
        params: { jobId },
        search: { thread: urlUserDraft.threadId },
      });
      return;
    } else {
      setComposeSeed(urlUserDraft);
      setSelectedUserDraftId(urlUserDraft._id);
      setComposing(true);
    }
    void navigate({
      to: "/dashboard/jobs/$jobId/mail",
      params: { jobId },
      search: (prev: { thread?: string; draft?: string; settings?: true }) => ({
        ...prev,
        draft: undefined,
      }),
    });
  }, [draftParamId, urlUserDraft, jobId, navigate]);

  const handleSelectUserDraft = (draft: UserDraftListItem) => {
    setSelectedDraftId(null);
    if (draft.threadId) {
      // Reply draft: open its thread; the reply composer resumes the draft.
      setComposing(false);
      setSelectedUserDraftId(draft._id);
      setSelectedThread(draft.threadId);
    } else {
      setSelectedThread(null);
      setComposeSeed(draft);
      setSelectedUserDraftId(draft._id);
      setComposing(true);
    }
  };

  // Start a fresh inline compose: clear any open thread/draft so the composer
  // owns the reading pane. `setSelectedThread(null)` also drops the settings
  // search param, closing the settings view.
  const handleStartCompose = () => {
    setSelectedDraftId(null);
    setComposeSeed(null);
    setSelectedUserDraftId(null);
    setSelectedThread(null);
    setComposing(true);
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(mailbox.emailAddress);
      setCopied(true);
      toast.success("Address copied");
    } catch {
      toast.error("Could not copy address");
    }
  };
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const handleFolderChange = (next: MailFolder) => {
    setFolder(next);
    if (next !== "drafts") setSelectedDraftId(null);
    if (next !== "userDrafts") setSelectedUserDraftId(null);
  };

  const showDraftReader = folder === "drafts";

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-muted">
      <div className="flex items-center justify-between gap-3 border-b bg-white px-6 py-4 dark:bg-card">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-full bg-primary/10">
            <Mail className="size-4 text-primary" />
          </div>
          <button
            type="button"
            onClick={copyAddress}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {mailbox.emailAddress}
            {copied ? (
              <Check className="size-3.5 text-emerald-500" />
            ) : (
              <Copy className="size-3.5" />
            )}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={chatOpen ? "secondary" : "outline"}
            onClick={() => setChatOpen((v) => !v)}
            className="gap-1.5"
          >
            <MessageSquare className="h-3.5 w-3.5" /> Chat
          </Button>
          <Button
            size="sm"
            variant={composing ? "secondary" : "default"}
            onClick={handleStartCompose}
          >
            <Pencil className="mr-1.5 size-3.5" />
            New email
          </Button>
          <Button
            size="icon"
            variant={settingsOpen ? "secondary" : "ghost"}
            className="size-8"
            onClick={() => setSettingsOpen(!settingsOpen)}
            title="Mailbox settings"
            aria-label="Mailbox settings"
          >
            <Settings className="size-4" />
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {settingsOpen ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <MailboxSettingsTab mailbox={mailbox} />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 gap-4 p-4">
            <div className="flex w-[400px] shrink-0 flex-col overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card">
              <MailListPane
                jobId={jobId}
                folder={folder}
                onFolderChange={handleFolderChange}
                selectedThreadId={selectedThreadId}
                onSelectThread={setSelectedThread}
                selectedDraftId={selectedDraftId}
                onSelectDraft={handleSelectDraft}
                selectedUserDraftId={selectedUserDraftId}
                onSelectUserDraft={handleSelectUserDraft}
                pendingDraftCount={pendingDrafts ?? 0}
                unreadCount={mailbox.unreadCount}
                userDraftCount={userDraftCount ?? 0}
              />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-card">
              {composing ? (
                <ComposeReader
                  key={composeSeed?._id ?? "new"}
                  jobId={jobId}
                  existingDraft={composeSeed}
                  mailboxEmail={mailbox.emailAddress}
                  onClose={() => setComposing(false)}
                  onDraftWithMorgan={(instruction) => {
                    // Draft with Morgan always begins in a clean chat so the
                    // request isn't buried under prior sweeps/conversation.
                    void (async () => {
                      try {
                        await startNewMailChat({ jobId });
                      } catch (err) {
                        toast.error(
                          extractErrorMessage(
                            err,
                            "Failed to start a new chat",
                          ),
                        );
                      }
                      setChatSeed(instruction);
                      setChatOpen(true);
                    })();
                  }}
                />
              ) : showDraftReader ? (
                selectedDraftId ? (
                  <DraftReader
                    jobId={jobId}
                    draftId={selectedDraftId}
                    onOpenContact={setViewingContactId}
                  />
                ) : (
                  <ReaderEmptyState
                    title="No draft selected"
                    message="Choose a draft from the list to review it here."
                  />
                )
              ) : selectedThreadId ? (
                <ThreadView
                  jobId={jobId}
                  threadId={selectedThreadId}
                  mailboxEmail={mailbox.emailAddress}
                  onOpenContact={setViewingContactId}
                />
              ) : (
                <ReaderEmptyState
                  title="Nothing selected"
                  message="Choose a thread from the list to read it here."
                />
              )}
            </div>
          </div>
        )}
      </div>

      <MailAgentChatPanel
        jobId={jobId}
        open={chatOpen}
        onOpenChange={(next) => {
          setChatOpen(next);
          if (!next) setChatSeed(null);
        }}
        initialInput={chatSeed ?? undefined}
        onReviewDrafts={(draftId) => {
          setSettingsOpen(false);
          setFolder("drafts");
          if (draftId) setSelectedDraftId(draftId);
        }}
      />

      {/* Full contact detail slide-out (opened from sender / participant
          clicks). The popover stays for unmatched addresses only. */}
      <ContactDetailPanel
        jobId={jobId}
        contactId={viewingContactId}
        open={viewingContactId !== null}
        onClose={() => setViewingContactId(null)}
        jobContacts={(jobContacts ?? []) as Array<JobContactRow>}
      />
    </div>
  );
}

function ReaderEmptyState({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted">
        <Mail className="size-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}
